import { ISchedule } from '@/models/IFunctionality';
import { Chip } from '@destination/components';
import { TFunction } from 'i18next';
import { ReactNode } from 'react';

export function translateSchedule(
  schedule: ISchedule,
  t: TFunction
): ReactNode {
  return (
    <>
      {schedule.value.frequency && (
        <div>
          {t('schedule.update_frequency', {
            frequency: parseISO8601(schedule.value.frequency, t)
          })}
        </div>
      )}

      {schedule.value.cron && (
        <div>
          {t('schedule.cron_expression', { expression: schedule.value.cron })}
        </div>
      )}

      {schedule.value.on_event && (
        <div>{parseOnEvent(schedule.value.on_event, t)}</div>
      )}
    </>
  );
}

function parseOnEvent(
  on_event: {
    max_age: string;
    event_names?: string[];
    hysteresis: {
      threshold?: number;
      properties: string[];
    }[];
  },
  t: TFunction
): ReactNode {
  return (
    <>
      <div>{t('schedule.event_occurs')}:</div>

      {on_event.max_age == 'NEVER' ? (
        <div className="ml-5">{t('schedule.max_wait_infinite')}</div>
      ) : (
        <div className="ml-5">
          {t('schedule.max_wait')}
          <Chip label={parseISO8601(on_event.max_age, t)} className="ml-2" />
        </div>
      )}

      {on_event.event_names && on_event.event_names.length > 0 && (
        <div className="ml-5">
          {t('schedule.triggered_events')}: &nbsp;
          <Chip label={on_event.event_names.join(', ')} />
        </div>
      )}

      {on_event.hysteresis && on_event.hysteresis.length > 0 && (
        <div className="ml-5">
          {t('schedule.hysteresis_conditions')}:
          {on_event.hysteresis.map(hysteresis => (
            <div key={hysteresis.threshold}>
              {hysteresis.threshold && (
                <div className="ml-5">
                  {t('schedule.threshold')}: &nbsp;
                  <Chip label={hysteresis.threshold.toString()} />
                </div>
              )}

              <div className="ml-5">
                {t('schedule.checked_properties')}: &nbsp;
                {hysteresis.properties.map(property => (
                  <Chip label={property.replace('$.', '')} className="mr-2" />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export function interpretDurationPattern(
  pattern: string,
  t: TFunction
): string {
  const parsedDuration = parseISO8601(pattern, t);
  if (parsedDuration) {
    return `${t('retention.duration')} ${parsedDuration}`;
  }
  return t('invalid');
}

function parseISO8601(duration: string, t: TFunction): string {
  const regExp = new RegExp(
    '^P(?!$)(?:([-+]?[0-9]+)D)?(T(?!$)(?:([-+]?[0-9]+)H)?(?:([-+]?[0-9]+)M)?(?:([-+]?[0-9]+)(?:[.,]([0-9]{0,9}))?S)?)?$'
  );
  const match = regExp.exec(duration);
  if (match) {
    let description = '';
    const days = match[1] ? parseInt(match[1]) : 0;
    const hours = match[3] ? parseInt(match[3]) : 0;
    const minutes = match[4] ? parseInt(match[4]) : 0;
    const seconds = match[5] ? parseInt(match[5]) : 0;

    if (days > 0) {
      description += ` ${days} ${t('retention.duration_days')}`;
    }
    if (hours > 0) {
      description += ` ${hours} ${t('retention.duration_hours')}`;
    }
    if (minutes > 0) {
      description += ` ${minutes} ${t('retention.duration_minutes')}`;
    }
    if (seconds > 0) {
      description += ` ${seconds} ${t('retention.duration_seconds')}`;
    }

    return description;
  }
  return t('retention.invalid');
}

export function generateRandomValue(type: string) {
  switch (type) {
    case 'string':
      return 'string';
    case 'integer':
      return 123;
    case 'boolean':
      return true;
    case 'object':
      return { key: 'value' };
    case 'array':
      return ['value1', 'value2'];
    default:
      return 'unknown';
  }
}
